/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { Menu, X, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LanguageDropdown } from "../LanguageDropdown";
import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";

/** Identifies which mobile submenu is expanded; only one may be open at a time. */
type MobileSubmenuId = "about" | "industries" | "products" | null;

// Stable ids drive dropdown logic; labelKey resolves to the localized label.
const navLinks = [
	{ id: "about", labelKey: "nav.aboutUs", href: "/about-us" },
	{ id: "industries", labelKey: "nav.industries", href: "/industries" },
	{ id: "clientele", labelKey: "nav.clientele", href: "/clientele" },
	{ id: "products", labelKey: "nav.products", href: "/products" },
	{ id: "worldwide", labelKey: "nav.worldwideProjects", href: "/worldwide-projects" },
	{ id: "contact", labelKey: "nav.contactUs", href: "/contact-us" },
];

const industries = [
	{ labelKey: "industries.powerGeneration", href: "/industries/power-generation" },
	{ labelKey: "industries.oilGas", href: "/industries/oil-gas" },
	{ labelKey: "industries.renewableEnergy", href: "/industries/renewable-energy" },
	{ labelKey: "industries.dataCenter", href: "/industries/data-center" },
	{ labelKey: "industries.waterTreatment", href: "/industries/water-treatment" },
	{ labelKey: "industries.steelMetallurgy", href: "/industries/steel-metallurgy" },
	{ labelKey: "industries.chemicalProcessing", href: "/industries/chemical-fertilizer" },
	{
		labelKey: "industries.plasticInjectionMolding",
		href: "/industries/plastic-injection-molding",
	},
	{ labelKey: "industries.marineOffshore", href: "/industries/marine-offshore" },
	{
		labelKey: "industries.processIndustries",
		href: "/industries/process-industries",
	},
	{ labelKey: "industries.hvacSystems", href: "/industries/hvac-systems" },
];

const products = [
	{
		labelKey: "productsMenu.shellTube",
		href: "/products/shell-tube-heat-exchangers",
	},
	{
		labelKey: "productsMenu.airCooled",
		href: "/products/air-cooled-heat-exchangers",
	},
	{
		labelKey: "productsMenu.waterCooled",
		href: "/products/water-cooled-air-coolers",
	},
	{
		labelKey: "productsMenu.doublePipe",
		href: "/products/double-pipe-heat-exchangers",
	},
	{ labelKey: "productsMenu.recuperators", href: "/products/recuperators" },
	{ labelKey: "productsMenu.dryCoolers", href: "/products/dry-coolers" },
	{ labelKey: "productsMenu.onan", href: "/products/onan" },
];

const about = [
	{ labelKey: "about.ourStory", href: "/our-story" },
	{ labelKey: "about.visionMission", href: "/vision-mission" },
	{ labelKey: "about.achievements", href: "/achievements" },
	{ labelKey: "about.socialInitiatives", href: "/social-initiatives" },
	{ labelKey: "about.founderMessage", href: "/founder-message" },
	{ labelKey: "about.managementTeam", href: "/management-team" },
	{ labelKey: "about.certifications", href: "/certifications" },
	{ labelKey: "about.gallery", href: "/gallery" },
];

interface NavItem {
	labelKey: string;
	href: string;
}

/**
 * Maps a top-level nav id to its child links. Rendering the mobile drawer from
 * this table (instead of three near-identical hand-written blocks) is what lets
 * a single `openMobileSubmenu` value guarantee only one panel is ever expanded.
 */
const MOBILE_SUBMENUS: Partial<Record<string, readonly NavItem[]>> = {
	about,
	industries,
	products,
};

interface DropdownProps {
	isOpen: boolean;
	onOpen: () => void;
	onClose: () => void;
	onClick: () => void;
	title: string;
	items: NavItem[];
	isScrolled: boolean;
	isHome: boolean;
}

function GenericDropdown({
	isOpen,
	onOpen,
	onClose,
	onClick,
	isHome,
	title,
	items,
	isScrolled,
}: DropdownProps) {
	const { t } = useTranslation();
	const dropdownRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node) &&
				triggerRef.current &&
				!triggerRef.current.contains(event.target as Node)
			) {
				onClose();
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [onClose]);

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				onClose();
			}
		};

		if (isOpen) {
			document.addEventListener("keydown", handleKeyDown);
			return () => document.removeEventListener("keydown", handleKeyDown);
		}
	}, [isOpen, onClose]);

	const linkColor =
		isScrolled || !isHome ?
			"text-black/70 hover:text-black"
		:	"text-black hover:text-black";

	return (
		<div className="relative">
			<div
				ref={triggerRef}
				className={`cursor-pointer text-sm ${
					isScrolled ?
						"text-black/70 hover:text-black"
					:	"text-black	 hover:text-black"
				}
				transition-colors duration-300 flex items-center gap-1`}
				onMouseEnter={onOpen}
				onMouseLeave={onClose}
				onClick={onClick}
				aria-expanded={isOpen}
				aria-haspopup="menu"
				role="button"
				tabIndex={0}>
				{title}
				<motion.div
					animate={{ rotate: isOpen ? 180 : 0 }}
					transition={{ duration: 0.2 }}>
					<ChevronDown className="w-4 h-4" />
				</motion.div>
			</div>

			<AnimatePresence>
				{isOpen && (
					<motion.div
						ref={dropdownRef}
						initial={{ opacity: 0, y: 8, scale: 0.96 }}
						animate={{ opacity: 1, y: 0, scale: 1 }}
						exit={{ opacity: 0, y: 8, scale: 0.96 }}
						transition={{
							duration: 0.25,
							ease: [0.16, 1, 0.3, 1],
						}}
						className="absolute -left-24 top-full pt-6 z-50"
						role="menu"
						aria-label={t("nav.menuLabel", { title })}
						onMouseEnter={onOpen}
						onMouseLeave={onClose}>
						<div className="relative w-85">
							<div className="absolute inset-0 rounded-2xl bg-white/90 blur-xl opacity-60" />

							<div className="relative bg-white/90 backdrop-blur-2xl border border-white/30 rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.12)] p-5">
								<div className="text-xs font-medium text-foreground/50 mb-3 px-2">
									{title}
								</div>

								<div className="grid grid-cols-2 gap-2">
									{items.map((item, i) => (
										<motion.div
											key={item.href}
											initial={{ opacity: 0, y: 6 }}
											animate={{ opacity: 1, y: 0 }}
											transition={{
												delay: 0.04 * i,
												duration: 0.25,
												ease: "easeOut",
											}}
											role="menuitem">
											<Link
												to={item.href}
												className="group flex items-center justify-between text-sm text-foreground hover:text-foreground rounded-lg px-3 py-2.5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-foreground/20"
												onClick={onClick}>
												<span className="transition-transform duration-200 group-hover:translate-x-1">
													{t(item.labelKey)}
													<span className="opacity-0 group-hover:opacity-100 transition-all duration-200 translate-x-[-4px] group-hover:translate-x-0 text-xs">
														↗
													</span>
												</span>
												<span className="absolute inset-0 rounded-lg bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 -z-10" />
											</Link>
										</motion.div>
									))}
								</div>
							</div>
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
}

export function Navigation() {
	const [isScrolled, setIsScrolled] = useState(false);
	const [isMounted, setIsMounted] = useState(false);
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const [isIndustriesOpen, setIsIndustriesOpen] = useState(false);
	const [isProductsOpen, setIsProductsOpen] = useState(false);
	const [isAboutOpen, setIsAboutOpen] = useState(false);
	// Single source of truth for the mobile drawer: at most one submenu is
	// expanded, so opening one collapses whichever was previously open.
	const [openMobileSubmenu, setOpenMobileSubmenu] =
		useState<MobileSubmenuId>(null);
	const mobilePanelRef = useRef<HTMLDivElement>(null);
	const menuButtonRef = useRef<HTMLButtonElement>(null);
	const shouldReduceMotion = useReducedMotion();
	const { t } = useTranslation();

	const location = useLocation();

	const toggleMobileSubmenu = useCallback((id: Exclude<MobileSubmenuId, null>) => {
		setOpenMobileSubmenu((current) => (current === id ? null : id));
	}, []);

	const closeMobileMenu = useCallback(() => {
		setIsMobileMenuOpen(false);
		setOpenMobileSubmenu(null);
	}, []);

	useEffect(() => {
		setIsMounted(true);
	}, []);

	// Route changes always leave the drawer in a clean, closed state.
	useEffect(() => {
		closeMobileMenu();
	}, [location.pathname, closeMobileMenu]);

	// Keyboard support for the drawer: Escape closes (collapsing an open submenu
	// first, as users expect from nested disclosures) and Tab is kept inside the
	// panel for as long as it is open, which is what `aria-modal` promises.
	useEffect(() => {
		if (!isMobileMenuOpen) return;

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				if (openMobileSubmenu) {
					setOpenMobileSubmenu(null);
					return;
				}
				closeMobileMenu();
				menuButtonRef.current?.focus();
				return;
			}

			if (event.key !== "Tab") return;

			const panel = mobilePanelRef.current;
			if (!panel) return;

			const focusable = panel.querySelectorAll<HTMLElement>(
				'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
			);
			if (focusable.length === 0) return;

			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			const active = document.activeElement;

			if (event.shiftKey && (active === first || !panel.contains(active))) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && active === last) {
				event.preventDefault();
				first.focus();
			}
		};

		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [isMobileMenuOpen, openMobileSubmenu, closeMobileMenu]);

	// Move focus into the drawer when it opens so keyboard users land inside it.
	useEffect(() => {
		if (!isMobileMenuOpen) return;
		const id = window.requestAnimationFrame(() => {
			mobilePanelRef.current?.focus();
		});
		return () => window.cancelAnimationFrame(id);
	}, [isMobileMenuOpen]);

	useEffect(() => {
		const handleScroll = () => {
			setIsScrolled(window.scrollY > 20);
		};

		handleScroll();
		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	// Prevent body scroll when mobile menu is open
	useEffect(() => {
		if (isMobileMenuOpen) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "";
		}
		return () => {
			document.body.style.overflow = "";
		};
	}, [isMobileMenuOpen]);

	if (!isMounted) {
		return null;
	}

	const isHome = location.pathname === "/";

	const base = "fixed z-50 transition-all duration-500";

	const layout =
		isScrolled ? "top-4 left-4 right-4 px-4" : "top-0 left-0 right-0 px-0";

	const textColor = isScrolled ? "!text-black" : "!text-white";
	const linkColor =
		isScrolled ?
			"text-black/70 hover:text-black"
		:	"text-black hover:text-black";

	return createPortal(
		<header className={`${base} ${layout} ${textColor}`}>
			<motion.nav
				initial={{ opacity: 0, y: shouldReduceMotion ? 0 : -10 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5 }}
				className={`mx-auto transition-all duration-500 ${
					isScrolled || isMobileMenuOpen ?
						"bg-background/70 backdrop-blur-2xl border border-border/70 rounded-2xl shadow-[0_14px_34px_rgba(18,19,54,0.22)] max-w-315"
					:	"md:bg-background/70 md:max-w-full md:px-10  "
				}`}>
				<div
					className={`flex items-center justify-between transition-all duration-500 px-4 sm:px-6 lg:px-0 ${
						isScrolled ? "h-14 lg:px-8" : (
							"h-16 sm:h-20 md:max-w-315 md:mx-auto md:w-full"
						)
					}`}>
					<Link
						to="/"
						className="flex items-center gap-2">
						<motion.img
							whileHover={{ scale: 1.05 }}
							className={`transition-all duration-500 ${
								isScrolled ? "h-8 sm:h-12" : (
									"h-8 sm:h-16 bg-white sm:bg-transparent  rounded-8 ml-0 px-4 py-1 rounded-md"
								)
							}`}
							src="/images/logo1.png"
							alt="Logo"
						/>
					</Link>

					<div className="hidden md:flex items-center gap-8 lg:gap-12">
						{navLinks.map((link) => {
							if (link.id === "industries") {
								return (
									<GenericDropdown
										key={link.id}
										isOpen={isIndustriesOpen}
										onOpen={() => setIsIndustriesOpen(true)}
										onClose={() => setIsIndustriesOpen(false)}
										onClick={() => setIsIndustriesOpen(!isIndustriesOpen)}
										title={t("nav.industries")}
										items={industries}
										isScrolled={isScrolled}
										isHome={isHome}
									/>
								);
							}
							if (link.id === "products") {
								return (
									<GenericDropdown
										key={link.id}
										isOpen={isProductsOpen}
										onOpen={() => setIsProductsOpen(true)}
										onClose={() => setIsProductsOpen(false)}
										onClick={() => setIsProductsOpen(!isProductsOpen)}
										title={t("nav.products")}
										items={products}
										isScrolled={isScrolled}
										isHome={isHome}
									/>
								);
							}

							if (link.id === "about") {
								return (
									<GenericDropdown
										key={link.id}
										isOpen={isAboutOpen}
										onOpen={() => setIsAboutOpen(true)}
										onClose={() => setIsAboutOpen(false)}
										onClick={() => setIsAboutOpen(!isAboutOpen)}
										title={t("nav.aboutUs")}
										items={about}
										isHome={isHome}
										isScrolled={isScrolled}
									/>
								);
							}

							return (
								<motion.div key={link.id}>
									<Link
										to={link.href}
										className={`text-sm ${linkColor}`}>
										{t(link.labelKey)}
									</Link>
								</motion.div>
							);
						})}
					</div>

					<div className="hidden text-black md:flex items-center gap-4">
						<LanguageDropdown />
					</div>

					<div className="md:hidden text-black flex items-center gap-2 sm:gap-3">
						<LanguageDropdown />
						{/* Solid white pill keeps the icon legible on the deep-blue hero,
						    on light pages, and over photography alike. */}
						<motion.button
							ref={menuButtonRef}
							onClick={() =>
								isMobileMenuOpen ? closeMobileMenu() : setIsMobileMenuOpen(true)
							}
							whileTap={shouldReduceMotion ? {} : { scale: 0.92 }}
							whileHover={shouldReduceMotion ? {} : { scale: 1.06 }}
							transition={{ type: "spring", stiffness: 400, damping: 22 }}
							className="touch-target relative flex h-11 w-11 items-center justify-center rounded-full bg-white text-black shadow-[0_4px_16px_rgba(12,16,32,0.28)] ring-1 ring-black/10 transition-colors duration-200 hover:bg-white/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f09a38] focus-visible:ring-offset-2"
							aria-expanded={isMobileMenuOpen}
							aria-controls="mobile-nav-panel"
							aria-label={
								isMobileMenuOpen ? t("nav.closeMenu") : t("nav.toggleMenu")
							}>
							<AnimatePresence
								mode="wait"
								initial={false}>
								<motion.span
									key={isMobileMenuOpen ? "close" : "open"}
									className="flex items-center justify-center"
									initial={{ rotate: -90, opacity: 0 }}
									animate={{ rotate: 0, opacity: 1 }}
									exit={{ rotate: 90, opacity: 0 }}
									transition={{ duration: 0.18, ease: "easeOut" }}>
									{isMobileMenuOpen ?
										<X
											className="h-5 w-5"
											strokeWidth={2.25}
										/>
									:	<Menu
											className="h-5 w-5"
											strokeWidth={2.25}
										/>
									}
								</motion.span>
							</AnimatePresence>
						</motion.button>
					</div>
				</div>
			</motion.nav>
			{/* MOBILE MENU */}
			<AnimatePresence>
				{isMobileMenuOpen && (
					<>
						{/* Backdrop — a tap anywhere outside the panel dismisses the menu. */}
						<motion.div
							key="mobile-nav-backdrop"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.2 }}
							onClick={closeMobileMenu}
							aria-hidden="true"
							className="md:hidden fixed inset-0 z-40 bg-foreground/40 backdrop-blur-[2px]"
						/>

						<motion.div
							key="mobile-nav-panel"
							id="mobile-nav-panel"
							ref={mobilePanelRef}
							role="dialog"
							aria-modal="true"
							aria-label={t("nav.toggleMenu")}
							tabIndex={-1}
							initial={{ opacity: 0, x: shouldReduceMotion ? 0 : "100%" }}
							animate={{ opacity: 1, x: 0 }}
							exit={{ opacity: 0, x: shouldReduceMotion ? 0 : "100%" }}
							transition={{
								duration: shouldReduceMotion ? 0.15 : 0.32,
								ease: [0.22, 1, 0.36, 1],
							}}
							// `w-[86%]` guarantees a strip of backdrop stays tappable even on
							// a 320px screen, where a `max-w-sm` panel would cover everything.
							className="md:hidden fixed inset-y-0 right-0 z-40 flex w-[86%] max-w-sm flex-col bg-background shadow-[-12px_0_40px_rgba(12,16,32,0.24)] overflow-y-auto overflow-x-hidden overscroll-contain focus-visible:outline-none"
							style={{ WebkitOverflowScrolling: "touch" }}>
							{/* The fixed header renders above this panel (z-50 vs z-40), so
							    the content starts below it — otherwise the close button
							    would sit underneath the hamburger. */}
							<div className="flex min-h-full flex-col px-5 pb-[max(2rem,env(safe-area-inset-bottom))] pt-20 sm:px-6 sm:pt-24">
								<div className="mb-6 flex justify-end">
									<button
										type="button"
										onClick={closeMobileMenu}
										className="touch-target inline-flex items-center gap-2 rounded-full border border-border/70 px-4 py-2 text-sm font-medium text-foreground transition-colors duration-200 hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f09a38]"
										aria-label={t("nav.closeMenu")}>
										<X className="h-4 w-4" />
										<span>{t("nav.closeMenu")}</span>
									</button>
								</div>

								<nav
									className="flex flex-1 flex-col gap-1 pb-8"
									aria-label={t("nav.toggleMenu")}>
									{navLinks.map((link, i) => {
										const submenuItems = MOBILE_SUBMENUS[link.id];

										if (submenuItems) {
											const submenuId = link.id as Exclude<
												MobileSubmenuId,
												null
											>;
											const isOpen = openMobileSubmenu === submenuId;
											const panelId = `mobile-${submenuId}-menu`;

											return (
												<div
													key={link.id}
													className="w-full border-b border-border/50 last:border-b-0">
													<motion.button
														type="button"
														initial={{ opacity: 0, x: shouldReduceMotion ? 0 : 16 }}
														animate={{ opacity: 1, x: 0 }}
														transition={{ delay: i * 0.04 }}
														onClick={() => toggleMobileSubmenu(submenuId)}
														className="touch-target flex w-full items-center justify-between gap-3 py-3.5 text-left text-xl font-display text-foreground transition-colors duration-200 hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f09a38] focus-visible:ring-offset-2 sm:text-2xl"
														aria-expanded={isOpen}
														aria-controls={panelId}>
														<span>{t(link.labelKey)}</span>
														<motion.span
															className="flex shrink-0 items-center justify-center"
															animate={{ rotate: isOpen ? 180 : 0 }}
															transition={{ duration: 0.24, ease: "easeOut" }}>
															<ChevronDown className="h-5 w-5" />
														</motion.span>
													</motion.button>

													<AnimatePresence initial={false}>
														{isOpen && (
															<motion.div
																key={panelId}
																id={panelId}
																initial={{ height: 0, opacity: 0 }}
																animate={{ height: "auto", opacity: 1 }}
																exit={{ height: 0, opacity: 0 }}
																transition={{
																	duration: shouldReduceMotion ? 0 : 0.28,
																	ease: [0.22, 1, 0.36, 1],
																}}
																className="overflow-hidden">
																<ul className="flex flex-col gap-0.5 border-l border-border/60 pb-3 pl-4">
																	{submenuItems.map((item) => (
																		<li key={item.href}>
																			<Link
																				to={item.href}
																				onClick={closeMobileMenu}
																				className="touch-target flex items-center py-2.5 text-base text-foreground/70 transition-colors duration-200 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f09a38]">
																				{t(item.labelKey)}
																			</Link>
																		</li>
																	))}
																</ul>
															</motion.div>
														)}
													</AnimatePresence>
												</div>
											);
										}

										return (
											<motion.div
												key={link.id}
												initial={{ opacity: 0, x: shouldReduceMotion ? 0 : 16 }}
												animate={{ opacity: 1, x: 0 }}
												transition={{ delay: i * 0.04 }}
												className="w-full border-b border-border/50 last:border-b-0">
												<Link
													to={link.href}
													onClick={closeMobileMenu}
													className="touch-target flex items-center py-3.5 text-xl font-display text-foreground transition-colors duration-200 hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f09a38] sm:text-2xl">
													{t(link.labelKey)}
												</Link>
											</motion.div>
										);
									})}
								</nav>
							</div>
						</motion.div>
					</>
				)}
			</AnimatePresence>
		</header>,
		document.body,
	);
}
